"use client";

import React, { useEffect, useRef, useState } from "react";
import { Anchor, HeartHandshake, Users } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const coreValues = [{
  title: "Pure Ontspanning",
  description: "Bij VIP Company geloven we dat een bezoek aan de kapper meer is dan alleen een knipbeurt. Het is een moment voor jezelf, een ervaring van pure luxe en rust.",
  icon: HeartHandshake
}, {
  title: "Vakmanschap",
  description: "Met meer dan 10 jaar ervaring in de branche combineren wij technische perfectie met passie. Wij luisteren naar jouw wensen en kijken naar wat echt bij jou past.",
  icon: Anchor
}, {
  title: "Persoonlijk Advies",
  description: "Geen standaard protocollen, maar een coupe die jouw persoonlijkheid versterkt. Jouw glimlach in de spiegel is voor ons team de grootste beloning.",
  icon: Users
}];
export default function OnzeKernwaarden() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="114873" id="passie-voor-haar" className="bg-background py-[120px] overflow-hidden">
      <div className="container mx-auto px-6 md:px-12 lg:px-[120px] max-w-[1440px]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          
          {}
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="sticky top-32">
              <h2 className="text-foreground font-serif text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
                Passie voor haar,<br />oog voor detail.
              </h2>
              <div className="w-20 h-1 bg-primary mt-8 rounded-full opacity-70"></div>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <p className="text-muted-foreground font-sans text-lg md:text-xl leading-relaxed mb-16">
              In onze salon staat vakmanschap centraal. Wij streven ernaar om elke behandeling tot een meesterwerk te verheffen, waarbij persoonlijke aandacht en oog voor detail de basis vormen van ons succes sinds 2014.
            </p>

            <ul className="space-y-12">
              {coreValues.map((value, i) => <li key={i} data-index={i} className="flex flex-col sm:flex-row gap-6 sm:gap-8 group">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center transition-colors duration-400 group-hover:bg-primary/20">
                      <value.icon className="w-8 h-8 text-primary" strokeWidth={1.5} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-serif font-bold text-foreground mb-3">
                      {value.title}
                    </h3>
                    <p className="text-muted-foreground font-sans text-base md:text-lg leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </li>)}
            </ul>
          </div>

        </div>
      </div>
    </section>;
}